import React, { useState, useEffect } from 'react';
import { PolicyChangeForm, ErrorModal } from '.';
import {
  PolicyChange,
  ChevronUp,
  ChevronDownUnbolded,
  PolicyChangeDisabled,
  ChevronDownDisabled
} from '../../icons';
import { simpleGTMDataLayer } from '../../util/GTMHelpers';
import {
  BoundingBox,
  Dropdown,
  IconOuterBox,
  IconInnerBox,
  WhiteBox,
  BoldText,
  ChevronBox
} from './changePolicyStyles';
import { GET_CHANGE_POLICY_INFO_account_items_policy_valid_policy_change_messages } from './graphql/__generated__/GET_CHANGE_POLICY_INFO';
import { OutageModal } from '..';
import { termObj } from './DataContainer';

export interface dropdownUIProps {
  isOpen?: boolean;
  disabled?: boolean;
}

export interface textProps {
  active?: boolean;
}

export interface changePolicyDropdownProps {
  policyNumber: string;
  accountNumber: string;
  sourceSysCode: string;
  makeAPolicyChange: Function;
  maxEffectiveDate?: string | number | Date | null | undefined;
  minEffectiveDate?: string | undefined | null;
  policyType?: string;
  clChangeWithDocument: Function;
  renewalDate: string | null;
  errorMessage: string | null | undefined;
  status: string | undefined | null;
  returnedMessage?:
  | (GET_CHANGE_POLICY_INFO_account_items_policy_valid_policy_change_messages | null)[]
  | null;
  pcOutage: boolean;
  isLoading: boolean;
  setIsLoading: Function;
  isOpen: boolean;
  setIsOpen: Function;
  multipleChangesIndicator: boolean | undefined;
  readOnlyAccess: boolean | undefined;
  readOnlyFeatureFlag: boolean | undefined;
  isFarmPolicy: boolean;
  isCppLine: boolean;
  isWorkerCompLine: boolean;
  isCommercialLine: boolean;
  latestPolicy: any;
  periods: any;
  isChangeDisabled: boolean;
  query_env: string;
  line: string;
  uploadToken: string;
  laterRenewalExists: boolean;
  activePolicyChanges: boolean;
  setRunSubscription: Function;
  changeResponse: any;
  uploadDoc: Function;
  uploadError: any;
  openChanges: boolean;
  renewalTerms: termObj[];
  showRenewal: boolean;
  changeDisabledForWC: boolean;
  changeDisabledForCPP: boolean;
  migrated: boolean;
}

export default ({
  policyNumber,
  accountNumber,
  makeAPolicyChange,
  maxEffectiveDate,
  minEffectiveDate,
  policyType,
  clChangeWithDocument,
  renewalDate,
  errorMessage,
  status,
  returnedMessage,
  pcOutage,
  isLoading,
  setIsLoading,
  isOpen,
  setIsOpen,
  multipleChangesIndicator,
  readOnlyAccess,
  readOnlyFeatureFlag,
  isFarmPolicy,
  isCppLine,
  isWorkerCompLine,
  isCommercialLine,
  latestPolicy,
  periods,
  isChangeDisabled,
  query_env,
  sourceSysCode,
  line,
  uploadToken,
  laterRenewalExists,
  activePolicyChanges,
  setRunSubscription,
  changeResponse,
  uploadDoc,
  uploadError,
  openChanges,
  renewalTerms,
  showRenewal,
  changeDisabledForWC,
  changeDisabledForCPP,
  migrated
}: changePolicyDropdownProps) => {
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [outageModalOpen, setOutageModalOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [policyNumber]);

  useEffect(() => {
    if (errorMessage) {
      setShowErrorModal(true);
    }
  }, [errorMessage]);

  if (((isChangeDisabled && (!isWorkerCompLine) && (!isCppLine)) || ((isWorkerCompLine && changeDisabledForWC) || (isCppLine && changeDisabledForCPP)))) {
    setIsOpen(false);
  }

  return (
    <div>
      {showErrorModal ? (
        <ErrorModal
          setShowModal={setShowErrorModal}
          errorMessage={errorMessage}
        />
      ) : (
        <></>
      )}
      <BoundingBox>
        {outageModalOpen && (
          <OutageModal
            title="Change Policy is Unavailable"
            onClose={() => setOutageModalOpen(false)}
          >
            Unfortunately, due to server maintenance, changing your policy is
            currently unavailable. Please check back in 24 to 48 hours.
          </OutageModal>
        )}
        <Dropdown
          test-attr="policy-change-dropdown-form-wrapper"
          isOpen={isOpen}
          onClick={() => {
            if (pcOutage) {
              setOutageModalOpen(!outageModalOpen);
            } else {
              setIsOpen(!isOpen);
              simpleGTMDataLayer({
                event: 'openPolicyChangeDropdownClick',
                event_action: 'Button Click',
                event_category: window.location.pathname,
                event_label: 'openChangePolicyDropdown'
              });
              simpleGTMDataLayer({
                event: 'openPolicyChangeDropdownClickSnowplow',
                event_action: 'Button Click',
                event_category: `${window.location.pathname}/Snowplow`,
                event_label: 'openChangePolicyDropdown'
              });
            }
          }}
          disabled={readOnlyAccess && readOnlyFeatureFlag}
        >
          <IconOuterBox>
            <IconInnerBox>
              <>
                {readOnlyAccess && readOnlyFeatureFlag ? (
                  <PolicyChangeDisabled />
                ) : (
                  <PolicyChange />
                )}
              </>
            </IconInnerBox>
          </IconOuterBox>
          <WhiteBox>
            <BoldText>CHANGE THIS POLICY</BoldText>
            <ChevronBox>
              {isOpen ? (
                <ChevronUp />
              ) : (
                <>
                  {((readOnlyAccess && readOnlyFeatureFlag) || (isChangeDisabled && !isWorkerCompLine && !isCppLine) || (isWorkerCompLine && changeDisabledForWC) || (isCppLine && changeDisabledForCPP)) ? (
                    <ChevronDownDisabled />
                  ) : (
                    <ChevronDownUnbolded />
                  )}
                </>
              )}
            </ChevronBox>
          </WhiteBox>
        </Dropdown>
        {(isOpen) && (
          <PolicyChangeForm
            changePolicy={makeAPolicyChange}
            isOpen={isOpen}
            policyNumber={policyNumber}
            accountNumber={accountNumber}
            maxEffectiveDate={maxEffectiveDate}
            minEffectiveDate={minEffectiveDate}
            policyType={policyType}
            clChangeWithDocument={clChangeWithDocument}
            setIsOpen={setIsOpen}
            renewalDate={renewalDate}
            status={status}
            returnedMessage={returnedMessage}
            test-attr="policy-change-form"
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            multipleChangesIndicator={multipleChangesIndicator}
            isFarmPolicy={isFarmPolicy}
            isCommercialLine={isCommercialLine}
            isCppLine={isCppLine}
            isWorkerCompLine={isWorkerCompLine}
            latestPolicy={latestPolicy}
            periods={periods}
            query_env={query_env}
            source_system_code={sourceSysCode}
            line={line}
            uploadToken={uploadToken}
            laterRenewalExists={laterRenewalExists}
            activePolicyChanges={activePolicyChanges}
            setRunSubscription={setRunSubscription}
            changeResponse={changeResponse}
            uploadDoc={uploadDoc}
            uploadError={uploadError}
            openChanges={openChanges}
            renewalTerms={renewalTerms}
            showRenewal={showRenewal}
            migrated={migrated}
          />
        )}
      </BoundingBox>
    </div>
  );
};